<script setup lang="ts">
    import { SplideSlide } from '@splidejs/vue-splide';
    import type { Schemas } from '#shopware';
    import { MEDICE_STAFF_IDS } from '~/types/Groups';
    const props = defineProps<{
        product: Schemas['Product'];
    }>();
    const { apiClient } = useShopwareContext();
    const { detailedUser, fetchDetailedUser } = useDetailedUser();
    const { urlSegment } = useLanguage();
    const { getStoryblokProductLinks } = useStoryblokProductsCache();
    const { t } = useI18n();

    const crossSellingProducts = ref<Schemas['Product'][]>();
    const isLoading = ref(true);
    const { isLoggedIn } = useLoginState();
    const productLinks = ref<LinkMap[]>();

    if (props.product) {
        const storyblokProductStoriesKey = computed(() => `storyblokProductStories-${urlSegment.value}`);
        const { data, error } = await useAsyncData(storyblokProductStoriesKey, getStoryblokProductLinks);

        if (error.value) {
            throw createError({
                statusCode: 500,
                message: 'Error fetching product Stories',
                fatal: true
            });
        }

        productLinks.value = data.value;
    }

    const getCrosssellingProducts = async () => {
        const productStreamIds = new Set(props.product.streamIds);
        isLoading.value = true;

        const streamQueries = Array.from(productStreamIds).map((streamId) => ({
            type: 'contains',
            field: 'streamIds',
            value: streamId
        }));

        if (streamQueries.length === 0) {
            crossSellingProducts.value = [];
            isLoading.value = false;
            return;
        }

        try {
            const result = await apiClient.invoke('readProduct post /product', {
                body: {
                    filter: [
                        {
                            type: 'equals',
                            field: 'active',
                            value: true
                        },
                        {
                            type: 'equals',
                            field: 'parentId',
                            value: null
                        },
                        {
                            type: 'multi',
                            operator: 'or',
                            queries: streamQueries
                        },
                        {
                            type: 'not',
                            operator: 'and',
                            queries: [
                                {
                                    type: 'equals',
                                    field: 'id',
                                    value: props.product.id
                                }
                            ]
                        }
                    ],
                    limit: 24,
                    includes: {
                        product: [
                            'id',
                            'name',
                            'translated',
                            'stock',
                            'releaseDate',
                            'streamIds',
                            'optionIds',
                            'children',
                            'calculatedPrices',
                            'calculatedPrice',
                            'calculatedCheapestPrice',
                            'properties',
                            'categories',
                            'cover',
                            'deliveryTime',
                            'seoUrls'
                        ],
                        property_group_option: ['id', 'name', 'translated'],
                        product_media: ['id', 'media'],
                        media: ['id', 'url', 'alt', 'title', 'fileName', 'mimeType', 'thumbnails'],
                        media_thumbnail: ['id', 'url', 'width', 'height'],
                        delivery_time: ['id', 'name'],
                        seo_url: ['seoPathInfo']
                    },
                    associations: {
                        cover: {
                            associations: {
                                media: {}
                            }
                        },
                        properties: {},
                        categories: {},
                        deliveryTime: {},
                        seoUrls: {}
                    }
                }
            });

            if (result?.data?.elements) {
                if (!detailedUser.value || !MEDICE_STAFF_IDS.includes(detailedUser.value.groupId)) {
                    result.data.elements = result.data.elements.filter(
                        ({ categories }) =>
                            categories?.every(
                                (category: Record<string, string>) => category.name !== 'MEDICE exklusiv'
                            ) ?? true
                    );
                }
                result.data.elements = result.data.elements.filter((product: Schemas['Product']) =>
                    product.streamIds?.some(
                        (streamId) => productStreamIds.has(streamId) && props.product.id != product.id
                    )
                );
                result.data.elements = result.data.elements.filter(
                    (product: Schemas['Product']) =>
                        !product.name?.includes(props.product.name.substring(0, props.product.name.length - 3))
                );
                result.data.elements = result.data.elements.filter(({ optionIds }) => !optionIds);

                crossSellingProducts.value = result?.data?.elements as Schemas['Product'][];
            }
        } catch (error) {
            console.error('no products found', error);
        } finally {
            isLoading.value = false;
        }
    };
    getCrosssellingProducts();

    onMounted(async () => {
        if (isLoggedIn.value) {
            await fetchDetailedUser();
        }
    });

    const sliderOptions = computed(() => {
        return {
            perPage: 3,
            rewind: true,
            rewindByDrag: true,
            gap: '1rem',
            drag: crossSellingProducts.value && crossSellingProducts.value.length > 3,
            arrows: crossSellingProducts.value && crossSellingProducts.value.length > 3,
            breakpoints: {
                1024: {
                    perPage: 2,
                    arrows: crossSellingProducts.value && crossSellingProducts.value.length > 2
                },
                640: {
                    perPage: 1,
                    arrows: crossSellingProducts.value && crossSellingProducts.value.length > 1
                }
            }
        };
    });
</script>
<template>
    <div class="crossselling-container">
        <div v-if="crossSellingProducts && crossSellingProducts.length > 0">
            <h2 class="title">{{ t('product.crossselling') }}</h2>
            <LazySliderComponent :options="sliderOptions">
                <template #slides>
                    <SplideSlide
                        v-for="element in crossSellingProducts"
                        :key="element.id"
                        class="slide"
                    >
                        <ProductCard
                            :product="element"
                            :seo-link="
                                productLinks && productLinks.length > 0
                                    ? productLinks.find((e) => e.id === element.id)
                                    : null
                            "
                        />
                    </SplideSlide>
                </template>
            </LazySliderComponent>
        </div>
        <div
            v-else-if="isLoading"
            class="loading-skeleton --block --full-width"
        ></div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/detail-page/crossselling-slider.scss';
</style>
